import React from 'react'
import { useSelector } from 'react-redux';
import { RiArrowLeftDoubleLine, RiArrowLeftSLine, RiArrowRightDoubleLine, RiArrowRightSLine } from 'react-icons/ri';

import useWindowSize from '../../../utils/useWindowSize';
import { arClass } from '../../../utils/useArClass';

export default function Pagination({ totalPages, page, setPage, pageSize, totalPagesCount }) {
  const darkMode = useSelector(state => state.settings.darkMode)
  const defaultLanguage = useSelector(state => state.settings.defaultLanguage)
  const { width } = useWindowSize()
 
  
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Color scheme for dark/light mode
  const colors = {
  background: darkMode ? "bg-gray-800" : "bg-white",
  text: {
    primary: darkMode ? "text-gray-100" : "text-gray-800",
    secondary: darkMode ? "text-gray-400" : "text-gray-600",
    muted: darkMode ? "text-gray-500" : "text-gray-400",
  },
  button: {
    active: darkMode
      ? "bg-blue-600 text-white hover:bg-blue-500"
      : "bg-blue-600 text-white hover:bg-blue-500",
    inactive: darkMode
      ? "bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600"
      : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-300",
    disabled: darkMode
      ? "bg-gray-800 text-gray-500 border border-gray-700 cursor-not-allowed"
      : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed",
    hover: darkMode
      ? "hover:bg-gray-600 hover:border-gray-500"
      : "hover:bg-blue-50 hover:border-blue-300",
  },
};


  if (totalPagesCount === 0) {
    return (
      <div className={arClass(`my-5 p-4 rounded-xl text-center ${colors.background} ${colors.text.secondary}`)}>
        {
          defaultLanguage === "en" ? "No results found." : defaultLanguage === "fr" ? "Aucun résultat trouvé." : "لم يتم العثور على نتائج."
        }
      </div>
    )
  }

  const renderPaginationButtons = () => {
    const buttons = [];
    let maxButtons = 9;
    
    if (width < 600) {
      maxButtons = 4;
    } else if (width < 768) {
      maxButtons = 7;
    } else if (width < 1024) {
      maxButtons = 6;
    }

    const range = maxButtons - 1;

    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(renderPageButton(i));
      }
    } else {
      let startPage = Math.max(1, page - Math.floor(range / 2));
      let endPage = Math.min(totalPages, startPage + range - 1);

      if (endPage >= totalPages - 1) {
        startPage = totalPages - range;
        endPage = totalPages - 1;
      }

      for (let i = startPage; i <= endPage; i++) {
        buttons.push(renderPageButton(i));
      }

      if (endPage < totalPages) {
        buttons.push(renderPageButton(totalPages));
      }
    }

    return buttons;
  };

  const renderPageButton = (i) => (
    <button
      key={i}
      onClick={() => handlePageChange(i)}
      className={arClass(`
        flex items-center justify-center min-w-10 h-10  px-3 mx-1 rounded-xl font-semibold
        transition-all duration-200 transform hover:scale-105 active:scale-95
         max-sm:text-xs max-sm:mx-0.5 max-sm:min-w-8 max-sm:h-8 max-sm:px-2 max-sm:rounded-md
        ${i === page 
          ? `${colors.button.active} shadow-lg ring-2 ring-blue-400/50` 
          : `${colors.button.inactive} ${colors.button.hover}`
        }
      `)}
      type='button'
    >
      {i}
    </button>
  );

  const NavButton = ({ onClick, disabled, children, ariaLabel, className = "" }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={arClass(`
        flex items-center justify-center min-w-[40px] h-10 px-3 rounded-xl font-semibold
        transition-all duration-200 transform hover:scale-105 active:scale-95
        max-sm:min-w-[32px] max-sm:h-8 max-sm:px-2 max-sm:rounded-md
        ${disabled 
          ? colors.button.disabled 
          : `${colors.button.inactive} ${colors.button.hover}`
        }
        ${className}
      `)}
      aria-label={ariaLabel}
      aria-disabled={disabled}
      type='button'
    >
      {children}
    </button>
  );

  return (
    <div className={arClass(` flex flex-col xl:flex-row justify-between items-center gap-4 mt-6 p-4 rounded-2xl w-full max-sm:p-3 ${colors.background} shadow-sm border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`)}>
      
      {/* Results Info */}
      <div className={arClass(`text-sm font-medium ${colors.text.secondary} text-center lg:text-left max-sm:text-xs`)}>
        {defaultLanguage ==="en"? "Showing" : defaultLanguage ==="fr"? "Affichage de" : "عرض"} 
        <span className={arClass(`font-bold max-sm:text-xs ${colors.text.primary} mx-1`)}>
          {(page - 1) * pageSize + 1}
        </span> 
        {defaultLanguage ==="en"? "to" : defaultLanguage ==="fr"? "à" : "إلى"} 
        <span className={arClass(`font-bold max-sm:text-xs ${colors.text.primary} mx-1`)}>
          {Math.min(page * pageSize, totalPagesCount)}
        </span> 
        {defaultLanguage ==="en"? "of" : defaultLanguage ==="fr"? "de" : "من"} 
        <span className={arClass(`font-bold max-sm:text-xs ${colors.text.primary} mx-1`)}>
          {totalPagesCount}
        </span> 
        {defaultLanguage ==="en"? "results" : defaultLanguage ==="fr"? "résultats" : "نتائج"}
      </div>

      {/* Pagination Controls */}
      <div className={arClass(`flex items-center max-sm:justify-between gap-2 max-sm:w-full`)}>
        {/* First Page */}
        <NavButton
          onClick={() => handlePageChange(1)}
          disabled={page === 1}
          ariaLabel='Go to first page'
          className={arClass("max-sm:hidden max-sm:rounded-md")}
        >
          {
            defaultLanguage === "ar" ? <RiArrowRightDoubleLine className={arClass("text-lg max-sm:rounded-md")} /> : <RiArrowLeftDoubleLine className={arClass("text-lg max-sm:rounded-md")} />
          }
        </NavButton>

        {/* Previous Page */}
        <NavButton
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          ariaLabel='Go to previous page'
        >
          {
            defaultLanguage === "ar" ? <RiArrowRightSLine className={arClass("text-lg max-sm:rounded-md")} /> : <RiArrowLeftSLine className={arClass("text-lg max-sm:rounded-md")} />
          }
        </NavButton>

        {/* Page Numbers */}
        <div className={arClass("flex items-center gap-1 mx-2")}>
          {renderPaginationButtons()}
        </div>

        {/* Next Page */}
        <NavButton
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages}
          ariaLabel='Go to next page'
        >
          {
            defaultLanguage === "ar" ? <RiArrowLeftSLine className={arClass("text-lg max-sm:rounded-md")} /> : <RiArrowRightSLine className={arClass("text-lg max-sm:rounded-md")} />
          }
        </NavButton>

        {/* Last Page */}
        <NavButton
          onClick={() => handlePageChange(totalPages)}
          disabled={page === totalPages}
          ariaLabel='Go to last page'
          className={arClass("max-sm:hidden max-sm:rounded-md")}
        >
          {
            defaultLanguage === "ar" ? <RiArrowLeftDoubleLine className={arClass("text-lg max-sm:rounded-md")} /> : <RiArrowRightDoubleLine className={arClass("text-lg max-sm:rounded-md")} />
          }
        </NavButton>
      </div>

      {/* Mobile Page Info */}
      <div className={arClass(`max-sm:w-full max-sm:flex max-sm:items-center max-sm:justify-between max-sm:gap-2 lg:hidden text-xs font-medium ${colors.text.muted} text-center`)}>
        <NavButton
          onClick={() => handlePageChange(1)}
          disabled={page === 1}
          ariaLabel='Go to first page'
          className={arClass("max-sm:flex hidden max-sm:rounded-md")}
        >
          {
            defaultLanguage === "ar" ? <RiArrowRightDoubleLine className={arClass("text-lg max-sm:rounded-md")} /> : <RiArrowLeftDoubleLine className={arClass("text-lg max-sm:rounded-md")} />
          }
        </NavButton>
        {
          defaultLanguage === "ar" ? "الصفحة" : defaultLanguage === "fr" ? "Page" : "Page"
        } {page} {
          defaultLanguage === "ar" ? "من" : defaultLanguage === "fr" ? "de" : "of"
        } {totalPages}
        {/* Last Page */}
        <NavButton
          onClick={() => handlePageChange(totalPages)}
          disabled={page === totalPages}
          ariaLabel='Go to last page'
          className={arClass("max-sm:flex hidden max-sm:rounded-md")}
        >
          {
            defaultLanguage === "ar" ? <RiArrowLeftDoubleLine className={arClass("text-lg max-sm:rounded-md")} /> : <RiArrowRightDoubleLine className={arClass("text-lg max-sm:rounded-md")} />
          }
        </NavButton>
      </div>
    </div>
  )
}